import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToFirstCapital } from "../../Utils/helpers";
import { viewSiteVisit } from "../../Redux/Actions/siteVisitActions";
import { sendNotification } from "../../Redux/Actions/notificationActions";
import axios from "axios";
import toast from "react-hot-toast";
import MainLayout from "../../Layout/mainLayout";
import "./siteVisit.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ViewSiteVisit = () => {
  const { id } = useParams();
  const { siteVisit } = useSelector((state) => state.siteVisit);
  const dispatch = useDispatch();

  const [tripStatus, setTripStatus] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(viewSiteVisit(id)).finally(() => setLoading(false));
  }, [id, dispatch]);

  useEffect(() => {
    if (siteVisit?.data?.trip_status) {
      setTripStatus(siteVisit.data.trip_status);
    }
  }, [siteVisit]);

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setShowConfirm(true);
  };

  const confirmStatusChange = async () => {
    setIsUpdating(true);
    setShowConfirm(false);
    try {
      await axios.post(`${BASE_URL}/site-visit/update`, {
        id: siteVisit?.data?._id,
        status: selectedStatus,
      });

      toast.success("Status updated successfully!");

      try {
        await dispatch(
          sendNotification({
            title: "Site Visit Status Updated",
            message: `Your site visit status has been updated to "${ToFirstCapital(selectedStatus)}".`,
            type_name: "none",
            type_id: "a1b2c3d4e5f678901234abcd",
            type: "selected",
            notification_for: "user",
            list: [siteVisit?.data?.user?._id],
            type_data: {
              site_visit_id: siteVisit?.data?._id,
              status: selectedStatus,
              project_name: siteVisit?.data?.project?.project_name || "",
              site_visit_date_time: siteVisit?.data?.pickup_date_time || "",
            },
          })
        );
      } catch (notificationError) {
        console.error("Notification failed:", notificationError);
      }

      await dispatch(viewSiteVisit(id));
      setTripStatus(selectedStatus);
    } catch {
      toast.error("Error updating status");
    } finally {
      setIsUpdating(false);
    }
  };

  const data = siteVisit?.data;
  const isSelf = data?.site_visit_by === "self";

  if (loading) {
    return (
      <MainLayout>
        <div className="vsv-loader-wrap">
          <div className="vsv-loader" />
          <p className="vsv-loader-text">Loading site visit data…</p>
        </div>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <div className="vsv-empty-wrap">
          <div className="vsv-empty-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </div>
          <p className="vsv-empty-text">No Site Visit Data Found.</p>
          <Link to="/site_visit" className="vsv-back-link">← Back to Site Visits</Link>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="vsv-wrapper">

        {/* ── Header ── */}
        <div className="vsv-header">
          <div className="vsv-header-left">
            <Link to="/site_visit" className="vsv-back-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </Link>
            <div className="vsv-header-divider" />
            <div>
              <div className="vsv-breadcrumb">Site Visit / List / View</div>
              {/* <h1 className="vsv-title">{ToFirstCapital(data?.ride_type) || "Site Visit"}</h1> */}
            </div>
          </div>
          <div className={`vsv-status-badge vsv-status-badge--${data?.status?.toLowerCase()}`}>
            {ToFirstCapital(data?.status || "")}
          </div>
        </div>

        {/* ── Hero Card ── */}
        <div className="vsv-hero-card">
          <div className="vsv-hero-left">
            <div>
              <div className="vsv-hero-label">Project</div>
              <div className="vsv-hero-value">{data?.project?.project_name || "—"}</div>
            </div>
            {data?.otp && (
              <div className="vsv-otp-badge">
                <span className="vsv-otp-label">OTP</span>
                {data.otp}
              </div>
            )}
          </div>
          <div className="vsv-hero-meta">
            <div className="vsv-hero-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {data?.site_visit_date_time
                ? new Date(data.site_visit_date_time).toLocaleString("en-IN", {
                  day: "2-digit", month: "short", year: "numeric",
                  hour: "2-digit", minute: "2-digit", hour12: true,
                  timeZone: "UTC",
                })
                : data?.pickup_date_time
                  ? new Date(data.pickup_date_time).toLocaleString("en-IN", {
                    day: "2-digit", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit", hour12: true,
                    timeZone: "Asia/Kolkata",
                  })
                  : "—"}
            </div>
            <div className="vsv-hero-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
              </svg>
              {data?.user?.name || "—"}
            </div>
            <div className="vsv-hero-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {ToFirstCapital(data?.site_visit_by) || "—"}
            </div>
          </div>
        </div>

        {/* ── Main Grid ── */}
        <div className="vsv-grid-layout">

          {/* Left Column */}
          <div className="vsv-col">

            {/* User Info */}
            <div className="vsv-card">
              <div className="vsv-card-header">
                <span className="vsv-card-badge">01</span>
                <h2 className="vsv-card-title">User Information</h2>
              </div>
              <div className="vsv-info-grid">
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Full Name</span>
                  <span className="vsv-info-value">{data?.user?.name || "No Data"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Email</span>
                  <span className="vsv-info-value">{data?.user?.email || "No Data"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Contact Number</span>
                  <span className="vsv-info-value">
                    {data?.user?.country_code} {data?.user?.contact_number || "No Data"}
                  </span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Status</span>
                  <span className={`vsv-inline-status vsv-inline-status--${data?.status?.toLowerCase()}`}>
                    {ToFirstCapital(data?.status)}
                  </span>
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="vsv-card">
              <div className="vsv-card-header">
                <span className="vsv-card-badge">02</span>
                <h2 className="vsv-card-title">Project Details</h2>
              </div>
              <div className="vsv-info-grid">
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Project Name</span>
                  <span className="vsv-info-value">{data?.project?.project_name || "No Data"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Site Visit By</span>
                  <span className="vsv-info-value">{ToFirstCapital(data?.site_visit_by) || "No Data"}</span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">Visit Date & Time</span>
                  <span className="vsv-info-value">
                    {data?.site_visit_date_time
                      ? new Date(data.site_visit_date_time).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                        timeZone: "UTC",
                      })
                      : data?.pickup_date_time
                        ? new Date(data.pickup_date_time).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                          timeZone: "Asia/Kolkata",
                        })
                        : "No Data"}
                  </span>
                </div>
                <div className="vsv-info-item">
                  <span className="vsv-info-label">No. of Guests</span>
                  <span className="vsv-info-value">{data?.no_of_guest ?? 0}</span>
                </div>
                {!isSelf && (
                  <>
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Ride Type</span>
                      <span className="vsv-info-value">{ToFirstCapital(data?.ride_type) || "No Data"}</span>
                    </div>
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Round Trip</span>
                      <span className="vsv-info-value">{data?.is_round_trip ? "Yes" : "No"}</span>
                    </div>
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Distance</span>
                      <span className="vsv-info-value">{data?.distance || 0} km</span>
                    </div>
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Estimated Price</span>
                      <span className="vsv-info-value">
                        {data?.estimated_price ? `₹${data.estimated_price}` : "No Data"}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Purpose */}
            {data?.site_visit_purpose && (
              <div className="vsv-card">
                <div className="vsv-card-header">
                  <span className="vsv-card-badge">03</span>
                  <h2 className="vsv-card-title">Visit Purpose</h2>
                </div>
                <p className="vsv-purpose-text">{data.site_visit_purpose}</p>
              </div>
            )}

          </div>

          {/* Right Column */}
          <div className="vsv-col">

            {/* Addresses */}
            <div className="vsv-card">
              <div className="vsv-card-header">
                <span className="vsv-card-badge">04</span>
                <h2 className="vsv-card-title">Location Details</h2>
              </div>
              <div className="vsv-address-row">
                {!isSelf && data?.pickup_address && (
                  <div className="vsv-address-item">
                    <div className="vsv-address-icon">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </div>
                    <div>
                      <div className="vsv-address-label">Pickup Address</div>
                      <div className="vsv-address-val">{data.pickup_address}</div>
                    </div>
                  </div>
                )}
                <div className="vsv-address-item">
                  <div className="vsv-address-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div>
                    <div className="vsv-address-label">
                      {isSelf ? "Project Address" : "Drop Off Address"}
                    </div>
                    <div className="vsv-address-val">
                      {data?.drop_address || data?.project?.street_address || "No Data"}
                    </div>
                  </div>
                </div>
                {/* <div className="vsv-address-item">
                  <div className="vsv-address-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <path d="M9 9h6M9 12h6M9 15h4" />
                    </svg>
                  </div>
                  <div>
                    <div className="vsv-address-label">Project Address</div>
                    <div className="vsv-address-val">{data?.project?.street_address || "No Data"}</div>
                  </div>
                </div> */}
              </div>
            </div>

            {/* Trip Status */}
            <div className="vsv-card">
              <div className="vsv-card-header">
                <span className="vsv-card-badge">05</span>
                <h2 className="vsv-card-title">Trip Status</h2>
              </div>
              <div className="vsv-info-item" style={{ marginBottom: "12px" }}>
                <span className="vsv-info-label">Current Status</span>
                <span className={`vsv-inline-status vsv-inline-status--${data?.status?.toLowerCase()}`} style={{ marginTop: "4px" }}>
                  {ToFirstCapital(data?.status)}
                </span>
              </div>
              <div className="vsv-info-item">
                <span className="vsv-info-label">Update Trip Status</span>
                <div className="vsv-select-wrap" style={{ marginTop: "6px" }}>
                  <select
                    value={tripStatus}
                    onChange={handleStatusChange}
                    className="vsv-select"
                    disabled={isUpdating}
                  >
                    <option value="" disabled>Select Status</option>
                    {["requested", "booked", "cancelled", "completed"].map((s) => (
                      <option key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </option>
                    ))}
                  </select>
                  <svg className="vsv-select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {isUpdating && (
                  <div className="vsv-updating-text">
                    <div className="vsv-updating-dot" />
                    Updating status…
                  </div>
                )}
              </div>
            </div>

            {/* Service Provider — only if not self */}
            {!isSelf && (
              <div className="vsv-card">
                <div className="vsv-card-header">
                  <span className="vsv-card-badge">06</span>
                  <h2 className="vsv-card-title">Service Provider</h2>
                </div>
                {data?.service_provider ? (
                  <div className="vsv-info-grid">
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Company Name</span>
                      <span className="vsv-info-value">{data.service_provider?.company_name || "No Data"}</span>
                    </div>
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Contact</span>
                      <span className="vsv-info-value">
                        {data.service_provider?.country_code} {data.service_provider?.contact_number || "No Data"}
                      </span>
                    </div>
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Email</span>
                      <span className="vsv-info-value">{data.service_provider?.email || "No Data"}</span>
                    </div>
                    <div className="vsv-info-item">
                      <span className="vsv-info-label">Address</span>
                      <span className="vsv-info-value">{data.service_provider?.address || "No Data"}</span>
                    </div>
                  </div>
                ) : (
                  <p className="vsv-no-data">No Service Provider Assigned</p>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ── Confirm Modal ── */}
      {showConfirm && (
        <div className="vsv-modal-backdrop">
          <div className="vsv-modal">
            <div className="vsv-modal-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 className="vsv-modal-title">Confirm Status Change</h3>
            <p className="vsv-modal-text">
              Are you sure you want to change the trip status to{" "}
              <strong>"{ToFirstCapital(selectedStatus)}"</strong>?
            </p>
            <div className="vsv-modal-actions">
              <button
                className="vsv-btn vsv-btn--confirm"
                onClick={confirmStatusChange}
                disabled={isUpdating}
              >
                Confirm
              </button>
              <button
                className="vsv-btn vsv-btn--cancel"
                onClick={() => setShowConfirm(false)}
                disabled={isUpdating}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ViewSiteVisit;